import { FileTextOutlined, SaveOutlined } from "@ant-design/icons";
import { Button, Modal } from "antd";
import { FC, useEffect, useMemo, useState } from "react";
import { normalizeProjectRequirementMarkdown } from "../../projectRequirement";
import MarkdownRequirementEditor from "../MarkdownRequirementEditor";
import "./index.css";

interface ProjectRequirementModalProps {
  editable?: boolean;
  onClose: () => void;
  onError?: (message: string) => void;
  onImageClick: (src: string) => void;
  onSave?: (requirement: string) => Promise<boolean>;
  open: boolean;
  projectName?: string;
  requirement?: string;
  saving?: boolean;
}

const ProjectRequirementModal: FC<ProjectRequirementModalProps> = ({
  editable = false,
  onClose,
  onError,
  onImageClick,
  onSave,
  open,
  projectName,
  requirement,
  saving = false,
}) => {
  const normalizedRequirement = useMemo(
    () => normalizeProjectRequirementMarkdown(requirement),
    [requirement]
  );
  const [draft, setDraft] = useState(normalizedRequirement);
  const [sourceCollapsed, setSourceCollapsed] = useState(true);

  useEffect(() => {
    if (open) {
      setDraft(normalizedRequirement);
      setSourceCollapsed(true);
    }
  }, [normalizedRequirement, open]);

  const hasChanges = draft.trim() !== normalizedRequirement.trim();

  async function handleSave() {
    if (!editable || !onSave || !hasChanges) {
      return;
    }
    const saved = await onSave(draft.trim());
    if (saved) {
      onClose();
    }
  }

  return (
    <Modal
      centered
      className="projectRequirementModal"
      destroyOnClose
      footer={
        <div className="projectRequirementModal__footer">
          <span>
            {editable
              ? "Markdown 内容会作为项目资料保存，并记录到项目日志。"
              : "你可以查看项目需求，但当前账号没有编辑项目资料的权限。"}
          </span>
          <div>
            <Button disabled={saving} onClick={onClose}>关闭</Button>
            {editable && (
              <Button
                disabled={!hasChanges}
                icon={<SaveOutlined />}
                loading={saving}
                onClick={handleSave}
                type="primary"
              >
                保存需求
              </Button>
            )}
          </div>
        </div>
      }
      keyboard={false}
      maskClosable={false}
      onCancel={onClose}
      open={open}
      title={
        <div className="projectRequirementModal__title">
          <FileTextOutlined />
          <div>
            <strong>项目需求</strong>
            <span>{projectName}</span>
          </div>
          <em>{editable ? "编辑模式" : "只读模式"}</em>
        </div>
      }
      width="min(1480px, calc(100vw - 32px))"
    >
      <MarkdownRequirementEditor
        ariaLabel="项目需求 Markdown"
        disabled={!editable}
        onChange={setDraft}
        onError={onError}
        onImageClick={onImageClick}
        onSourceCollapsedChange={setSourceCollapsed}
        sourceCollapsed={sourceCollapsed}
        value={draft}
      />
    </Modal>
  );
};

export default ProjectRequirementModal;
